import React, { useState } from 'react';
import { generatePath, NavLink, useHistory } from 'react-router-dom';
import ChatAvatar from 'flow/components/ui-components/ChatAvatar';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { useAppSelector } from 'flow/hooks/useRedux';
import { Message } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { getAttachmentLink, getFormattedTime } from 'flow/utils';
import {
    applyStyling,
    getStyledMessageBody,
    truncateText,
} from 'flow/utils/message-utils';
import { twMerge } from 'tailwind-merge';
import FromAvatar from './FromAvatar';
import MessageSender from './MessageSender';
import PostImageLightbox from './PostImageLightbox';

interface Props {
    message: TypedMap<Message>;
    sessionKey: ChatSessionKey;
    isSummary?: boolean;
    /** Shown on the Overview, see pathnameIsOverview. The caller reads the
     *  address, so that a change of the address does not render every post. */
    inOverview?: boolean;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

/** Whether the address is the Overview, where a post links to its chat. */
export const pathnameIsOverview = (pathname: string) =>
    pathname.endsWith(routes['messages']) ||
    pathname.endsWith(routes['messages.overview']);

const ChatPostDisplay = ({
    message,
    sessionKey,
    isSummary = false,
    inOverview = false,
    videoDark = false,
}: Props) => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();
    const history = useHistory();
    const { hasFeature } = useFeatureFlags();
    const [showImageLightbox, setShowImageLightbox] = useState(false);

    const session = useAppSelector((state) =>
        state.chats.getIn(['sessions', sessionKey]),
    );

    const attachment = message.get('attachment');
    const attachmentSource = attachment
        ? getAttachmentLink(sessionKey, attachment)
        : null;

    const openPostModal = (
        e:
            | React.MouseEvent<HTMLDivElement, MouseEvent>
            | React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ): void => {
        openModal('chatPost', { message, sessionKey });
        e.preventDefault();
        e.stopPropagation();
    };

    // Full post view: show the attachment as a nested Dialog rendered here
    // (not via the ModalProvider stack, which only renders its topmost entry
    // and would unmount ChatPostModal), since posts aren't part of the
    // session's 'images' content list that ImagePreviewModal relies on.
    const openImageLightbox = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ): void => {
        setShowImageLightbox(true);
        e.preventDefault();
        e.stopPropagation();
    };

    const navigateToChatSession = () => {
        history.push(
            generatePath(routes['messages.id'], {
                id: sessionKey,
            }),
        );
    };

    const getAttachmentClickHandler = () => {
        if (!isSummary) {
            return openImageLightbox;
        }
        return inOverview ? navigateToChatSession : openPostModal;
    };

    const getChatRoomDetails = () => {
        return (
            <div
                className={twMerge(
                    'flex items-center gap-2 rounded-sm p-2 shadow-lg',
                    videoDark ? 'bg-gray-600' : 'bg-white',
                    !attachment ? 'ms-6' : 'absolute start-6 top-6',
                )}
            >
                <ChatAvatar
                    session={session}
                    showUnreadBadge={session.get('numberOfUnreadMessages') > 1}
                />
                <p
                    className={twMerge(
                        'text-sm/6',
                        videoDark ? 'text-gray-100' : 'text-gray-800',
                    )}
                >
                    {session.get('name')}
                </p>
            </div>
        );
    };

    return (
        <>
            {attachment && (
                <div
                    className={twMerge(
                        isSummary || inOverview ? '' : 'px-6 pt-6',
                    )}
                >
                    <button
                        className={twMerge(
                            'relative flex max-h-[320px] w-full items-center justify-center overflow-hidden rounded-t-sm',
                            videoDark ? 'bg-gray-600' : 'bg-gray-150',
                        )}
                        onClick={getAttachmentClickHandler()}
                    >
                        <img
                            src={attachmentSource}
                            height={attachment.get('previewHeight')}
                            width={attachment.get('previewWidth')}
                            alt={attachment.get('fileName')}
                            className="max-h-full min-h-40 w-full object-contain"
                        />
                        {inOverview && getChatRoomDetails()}
                    </button>
                </div>
            )}

            {showImageLightbox && attachmentSource && (
                <PostImageLightbox
                    imageSrc={attachmentSource}
                    fileName={attachment.get('fileName')}
                    onClose={() => setShowImageLightbox(false)}
                />
            )}

            <>
                {!attachment && inOverview && (
                    <NavLink
                        to={generatePath(routes['messages.id'], {
                            id: session.get('key'),
                        })}
                    >
                        <div
                            className={twMerge(
                                'flex h-[84px] w-full items-center',
                                videoDark ? 'bg-gray-600' : 'bg-gray-200',
                            )}
                        >
                            {getChatRoomDetails()}
                        </div>
                    </NavLink>
                )}

                <div className="flex flex-col gap-y-6 p-6">
                    <h1
                        className={twMerge(
                            'text-2xl/6 font-bold',
                            videoDark ? 'text-white' : 'text-gray-800',
                        )}
                        dir="auto"
                    >
                        {message.get('title')}
                    </h1>
                    <div className="flex flex-row items-center gap-x-3">
                        <FromAvatar
                            from={message.get('from')}
                            avatarOnly={true}
                            size="small"
                            chatSessionKey={sessionKey}
                        />
                        <div className="flex-1">
                            <MessageSender
                                message={message}
                                sessionKey={sessionKey}
                            />
                        </div>
                        <time
                            className={twMerge(
                                'text-xs/4',
                                videoDark ? 'text-gray-400' : 'text-gray-500',
                            )}
                            dateTime={message.get('sent')}
                        >
                            {getFormattedTime(message.get('sent'), i18next)}
                        </time>
                    </div>

                    <p
                        dir="auto"
                        className={twMerge(
                            'leading-6 wrap-break-word whitespace-pre-wrap',
                            videoDark ? 'text-gray-300' : 'text-gray-800',
                        )}
                    >
                        {isSummary
                            ? applyStyling(
                                  truncateText(message.get('message')),
                                  undefined,
                                  sessionKey,
                              )
                            : getStyledMessageBody(
                                  message,
                                  sessionKey,
                                  hasFeature(FeatureFlags.MARKDOWN),
                              )}
                    </p>

                    {isSummary &&
                        truncateText(message.get('message')) !==
                            message.get('message') && (
                            <p className="mt-6" dir="auto">
                                <button
                                    className="text-link text-sm font-medium hover:underline"
                                    onClick={
                                        inOverview
                                            ? navigateToChatSession
                                            : openPostModal
                                    }
                                >
                                    {i18next.t('chat.post.message.more')}
                                </button>
                            </p>
                        )}
                </div>
            </>
        </>
    );
};

export default ChatPostDisplay;
